import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ShieldCheck } from "lucide-react";
import { PolicyPageShell } from "@/components/PolicyHeader";
import { publicApi } from "@/lib/api";

const LOAN = { personal_loan: "Personal Loan", business_loan: "Business Loan", home_loan: "Home Loan", loan_against_property: "Loan Against Property" };
const STEPS = ["pending", "paid", "generated", "sent"];
const LABEL = { pending: "Received", paid: "Verifying", generated: "Credit Review", sent: "Letter Released" };
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function TrackStatus() {
  const [sp] = useSearchParams();
  const [ref, setRef] = useState(sp.get("ref") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const lookup = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr(null); setData(null);
    try { setData((await publicApi.get(`/eligibility/track/${encodeURIComponent(ref.trim())}`)).data); }
    catch (ex) { setErr(ex?.response?.data?.detail?.message_en || "Lookup failed. Please check the reference."); }
    finally { setBusy(false); }
  };
  const idx = data ? STEPS.indexOf(data.status) : -1;
  return (
    <PolicyPageShell testId="track-page" eyebrow="Application Status" title="Track Your Eligibility Check"
      lead="Enter the Reference ID from your confirmation e-mail or receipt. Only status information is shown here — never your documents or identity numbers.">
      <div className="max-w-xl mx-auto pb-24 sm:pb-8">
        <form onSubmit={lookup} className="rounded-2xl border border-[#D4AF37]/25 bg-white/[0.03] p-6 space-y-4">
          <label className="block text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]/90">Reference ID
            <input data-testid="track-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="SF/NIAM/EL/2026/… or check id"
              className="mt-2 w-full rounded-xl bg-[#0A1F44] border border-white/15 focus:border-[#D4AF37] outline-none px-4 min-h-[48px] text-white text-base placeholder:text-white/30" />
          </label>
          <button data-testid="track-submit" type="submit" disabled={!ref.trim() || busy} className="btn-gold inline-flex items-center gap-2 min-h-[48px] px-6 rounded-full text-sm font-semibold disabled:opacity-40"><Search size={15} />{busy ? "Checking…" : "Check status"}</button>
          {err && <div data-testid="track-error" className="text-rose-200 text-sm">{err}</div>}
        </form>
        {data && (
          <div data-testid="track-result" className="mt-6 rounded-2xl border border-white/10 bg-[#0A1F44]/60 p-6 space-y-4">
            <div className="flex items-center gap-2 text-white/85 text-sm"><ShieldCheck size={16} className="text-emerald-300" /> Reference <span data-testid="track-reference" className="text-[#F0D788] font-mono">{data.reference}</span></div>
            <div className="grid grid-cols-4 gap-1">
              {STEPS.map((s, i) => (
                <div key={s} className="text-center">
                  <div className={`h-1.5 rounded-full ${i <= idx ? "bg-[#D4AF37]" : "bg-white/10"}`} />
                  <div className={`mt-1 text-[10px] uppercase tracking-wider ${i <= idx ? "text-[#F0D788]" : "text-white/40"}`}>{LABEL[s]}</div>
                </div>
              ))}
            </div>
            <div data-testid="track-status" className="text-white text-lg font-semibold">{data.status_text}</div>
            {data.progress_text && <div data-testid="track-progress" className="text-amber-200 text-sm">{data.progress_text}</div>}
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><dt className="text-white/50 text-[11px] uppercase tracking-wider">Submitted</dt><dd className="text-white/90">{when(data.submitted_at)}</dd></div>
              <div><dt className="text-white/50 text-[11px] uppercase tracking-wider">Loan type</dt><dd className="text-white/90">{LOAN[data.loan_type] || data.loan_type || "—"}</dd></div>
              <div><dt className="text-white/50 text-[11px] uppercase tracking-wider">Registered e-mail</dt><dd data-testid="track-email" className="text-white/90">{data.email_masked || "—"}</dd></div>
              <div><dt className="text-white/50 text-[11px] uppercase tracking-wider">Expected turnaround</dt><dd className="text-white/90">{data.expected_turnaround}</dd></div>
              {data.letter_released_at && <div><dt className="text-white/50 text-[11px] uppercase tracking-wider">Letter released</dt><dd className="text-white/90">{when(data.letter_released_at)}</dd></div>}
            </dl>
            {data.status === "sent" && <a href="/my-letter" className="inline-block text-sm text-[#F0D788] underline">Open the Customer Letter Portal to view your letter →</a>}
          </div>
        )}
      </div>
    </PolicyPageShell>
  );
}
